import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "../page.css";
import "../home.css";
import "./kilimanjaro.css";

const features = [
  {
    title: "Uhuru Peak, 5,895 m",
    text: "The highest point in Africa. Summit night is long and cold, so we pace the days below with care.",
  },
  {
    title: "Five climate zones",
    text: "Rainforest, moorland, alpine desert, and arctic summit. The mountain changes under your feet each day.",
  },
  {
    title: "Guided private crews",
    text: "Your guide, cooks, and porters stay with your group. We keep ratios clear and the schedule honest.",
  },
  {
    title: "Acclimatization first",
    text: "Extra rest days matter more than speed. We choose routes and durations that give your body time.",
  },
];

const routes = [
  {
    id: "machame",
    name: "Machame Route",
    days: "6 to 7 days",
    difficulty: "Challenging",
    lodging: "Camping",
    startGate: "Machame Gate",
    bestFor: "Scenery and a classic first climb",
    image:
      "https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Trekkers on an open ridge toward Kilimanjaro",
    summary:
      "The most popular scenic path on the southern slopes. You leave rainforest for open ridges, pass the Western Breach views, and climb the Barranco Wall before the final push from Barafu.",
    body: "Machame rewards groups who want variety day to day. Distances are manageable, but the terrain keeps changing, so pacing and water matter as much as fitness. We usually recommend seven days when schedules allow, so you gain height more gently before summit night.",
    highlights: [
      "Rainforest start with strong birdlife in the early hours",
      "Shira and Barranco camps with wide crater and glacier views",
      "Barranco Wall scramble that is steep but non technical with a guide",
      "Summit via Stella Point, then Uhuru Peak, with descent toward Mweka",
    ],
    itinerary: [
      { day: "Day 1", text: "Machame Gate to Machame Camp through rainforest." },
      { day: "Day 2", text: "Climb onto the Shira Plateau and rest at Shira Camp." },
      { day: "Day 3", text: "Cross the alpine desert toward Barranco Camp." },
      { day: "Day 4", text: "Barranco Wall, Karanga, then Barafu for summit staging." },
      { day: "Day 5", text: "Midnight start to Stella Point and Uhuru Peak, descend to Mweka." },
      { day: "Day 6", text: "Forest walk out to Mweka Gate and transfer to Moshi or Arusha." },
    ],
    notes:
      "A six day option exists for strong hikers, though seven days usually feels better at altitude.",
  },
  {
    id: "lemosho",
    name: "Lemosho Route",
    days: "7 to 8 days",
    difficulty: "Challenging",
    lodging: "Camping",
    startGate: "Londorossi Gate",
    bestFor: "Acclimatization and quieter early camps",
    image:
      "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Wide highland plateau under clear mountain sky",
    summary:
      "A longer western approach that begins in remote forest, opens onto the Shira Plateau, then joins the southern circuit used by Machame climbers for the final camps.",
    body: "Lemosho is our preferred choice when the group can spare an extra day. Early camps are quieter, the scenery is broad and open, and the added night at mid altitude helps many climbers feel stronger on summit night. Eight days is ideal; seven is still solid for fit groups.",
    highlights: [
      "West side start with fewer crowds in the first days",
      "Long traverse across the Shira Plateau",
      "Strong climb high, sleep low rhythm before Barranco",
      "Often chosen for comfort and summit confidence",
    ],
    itinerary: [
      { day: "Day 1", text: "Drive to Londorossi, short forest walk to Mti Mkubwa Camp." },
      { day: "Day 2", text: "Rise onto Shira Plateau and camp near Shira One." },
      { day: "Day 3", text: "Cross the plateau toward Shira Two or similar." },
      { day: "Day 4", text: "Lava Tower acclimatization day, sleep at Barranco." },
      { day: "Days 5 to 6", text: "Karanga and Barafu, with summit night from Barafu." },
      { day: "Days 7 to 8", text: "Descend via Mweka, with an optional rest day on longer plans." },
    ],
    notes:
      "Ask for the eight day plan if this is your first high altitude trek or if anyone in the group is over 50.",
  },
  {
    id: "marangu",
    name: "Marangu Route",
    days: "5 to 6 days",
    difficulty: "Moderate to challenging",
    lodging: "Mountain huts",
    startGate: "Marangu Gate",
    bestFor: "Hut lodging and a shorter schedule",
    image:
      "https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Mountain path through cloud and rock near the summit zone",
    summary:
      "The classic southeast route, often called the Coca Cola route. You sleep in shared mountain huts instead of tents, with a more direct line toward Kibo and Gilman’s Point.",
    body: "Marangu suits travelers who prefer fixed lodging and a simpler packing list for the mountain. The path is steady rather than dramatic, and you usually walk the same corridor out. Because five day plans move quickly, we push for six days whenever possible so you can rest at Mawenzi Hut before summit night.",
    highlights: [
      "Communal huts with bunks at Mandara, Horombo, and Kibo",
      "Gentler trail grade compared with ridge routes",
      "Less scenic variety than Machame or Lemosho",
      "Same trail up and down, which feels familiar on tired legs",
    ],
    itinerary: [
      { day: "Day 1", text: "Marangu Gate to Mandara Hut through rainforest." },
      { day: "Day 2", text: "Open moorland walk to Horombo Hut." },
      { day: "Day 3", text: "Optional acclimatization day near Mawenzi, or continue to Kibo." },
      { day: "Day 4", text: "Alpine desert to Kibo Hut, early summit start." },
      { day: "Day 5", text: "Gilman’s Point and Uhuru Peak, descend to Horombo." },
      { day: "Day 6", text: "Return to Marangu Gate and transfer to town." },
    ],
    notes:
      "Hut space is shared and can feel busy in peak months. Book early for July, August, and the Christmas window.",
  },
  {
    id: "rongai",
    name: "Rongai Route",
    days: "6 to 7 days",
    difficulty: "Moderate to challenging",
    lodging: "Camping",
    startGate: "Rongai Gate",
    bestFor: "Drier weather and a quieter approach",
    image:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Quiet alpine slopes on a remote mountain approach",
    summary:
      "A northern approach near the Kenyan border. Early days feel remote and often stay drier than the southern rainforest routes, before you join busier camps near the crater.",
    body: "Rongai is a strong pick in the wetter months, or for anyone who wants fewer people on the trail at the start. Gradients are kinder in the first half, then the climb steepens toward Kibo. Most plans summit via Gilman’s Point and descend on the Marangu side toward Horombo and Marangu Gate.",
    highlights: [
      "Northern start with open farmland and forest views",
      "Often drier than Machame during shoulder seasons",
      "Remote feel until Mawenzi and Kibo camps",
      "Descent shares the Marangu trail corridor",
    ],
    itinerary: [
      { day: "Day 1", text: "Transfer to Rongai Gate, walk to Simba Camp." },
      { day: "Day 2", text: "Steady climb toward Second Cave or Kikelewa." },
      { day: "Day 3", text: "Approach Mawenzi Tarn for strong acclimatization views." },
      { day: "Day 4", text: "Cross the saddle to Kibo Camp." },
      { day: "Day 5", text: "Summit night via Gilman’s Point to Uhuru, descend to Horombo." },
      { day: "Day 6", text: "Walk out to Marangu Gate, with a seventh day option for rest." },
    ],
    notes:
      "Ideal if your dates fall near the short rains, or if you want a quieter first half of the climb.",
  },
];

const practical = [
  {
    title: "Best months",
    text: "January to early March, and June to October, when trails are usually clearer and camps less crowded outside peak weeks.",
  },
  {
    title: "Fitness",
    text: "You do not need technical climbing skills. You do need steady cardio, strong legs, and the patience to walk slowly at altitude.",
  },
  {
    title: "What we arrange",
    text: "Park fees, crew, meals on the mountain, tents or huts by route, transfers from Moshi or Arusha, and a clear packing list before you fly.",
  },
];

function Kilimanjaro() {
  const { hash } = useLocation();

  useEffect(() => {
    document.title = "Mount Kilimanjaro · Mirrorkhan Tours";
  }, []);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [hash]);

  return (
    <div className="kili-page">
      <section className="kili-hero">
        <div className="kili-hero__media" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1609198092458-43ad304fbe58?auto=format&fit=crop&w=1800&q=80"
            alt=""
          />
        </div>
        <div className="kili-hero__shade" aria-hidden="true" />
        <div className="container kili-hero__content">
          <div className="row">
            <div className="col-lg-8 col-xl-7 mk-hero-enter">
              <p className="eyebrow eyebrow--light mb-3">
                <Link to="/trekking" className="text-decoration-none">
                  Trekking
                </Link>
              </p>
              <h1 className="mb-3">Mount Kilimanjaro</h1>
              <p className="kili-hero__lede mb-4">
                Africa’s highest mountain, climbed on foot with a private crew.
                We match the route and number of days to your fitness, season,
                and how you like to move on the trail.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <a className="btn btn-mk-gold" href="#routes">
                  View routes
                </a>
                <Link className="btn btn-mk-ghost" to="/contact">
                  Ask about a climb
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5" data-reveal>
            <div className="col-lg-8">
              <p className="eyebrow">The mountain</p>
              <h2 className="mb-3">Why Kilimanjaro stands alone</h2>
              <p className="page-lead mb-0">
                It is not a technical alpine climb. It is a high altitude trek
                where weather, hydration, and honest pacing decide the summit
                more than speed.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {features.map((item, index) => (
              <div
                className="col-md-6"
                key={item.title}
                data-reveal
                style={{ "--reveal-delay": `${(index % 2) * 100}ms` }}
              >
                <article className="kili-feature h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="routes" className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5" data-reveal>
            <div className="col-lg-8">
              <p className="eyebrow">Routes</p>
              <h2 className="mb-3">Choose the line that fits your group</h2>
              <p className="page-lead mb-0">
                Each route reaches Uhuru Peak. The difference is scenery, camp
                style, crowd levels, and how many days you give yourself to
                adapt.
              </p>
            </div>
          </div>

          <div className="kili-routes">
            {routes.map((route, index) => (
              <article
                className={
                  index % 2 === 1
                    ? "kili-route kili-route--flip"
                    : "kili-route"
                }
                key={route.id}
                id={route.id}
                data-reveal={index % 2 === 1 ? "right" : "left"}
              >
                <div className="kili-route__top">
                  <div className="kili-route__media">
                    <img src={route.image} alt={route.imageAlt} />
                  </div>
                  <div className="kili-route__intro">
                    <p className="kili-route__meta mb-2">
                      {route.days}
                      <span aria-hidden="true"> · </span>
                      {route.difficulty}
                    </p>
                    <h3 className="h3 mb-3">{route.name}</h3>
                    <p className="kili-route__summary">{route.summary}</p>
                    <p className="kili-route__body-copy mb-0">{route.body}</p>
                  </div>
                </div>

                <div className="kili-route__panel">
                  <dl className="kili-route__facts">
                    <div>
                      <dt>Best for</dt>
                      <dd>{route.bestFor}</dd>
                    </div>
                    <div>
                      <dt>Start gate</dt>
                      <dd>{route.startGate}</dd>
                    </div>
                    <div>
                      <dt>Lodging</dt>
                      <dd>{route.lodging}</dd>
                    </div>
                    <div>
                      <dt>Duration</dt>
                      <dd>{route.days}</dd>
                    </div>
                  </dl>

                  <div className="row g-4">
                    <div className="col-lg-5">
                      <h4 className="kili-route__subhead">On the trail</h4>
                      <ul className="trek-points mb-0">
                        {route.highlights.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="col-lg-7">
                      <h4 className="kili-route__subhead">Sample outline</h4>
                      <ol className="kili-itinerary list-unstyled mb-0">
                        {route.itinerary.map((step) => (
                          <li key={step.day}>
                            <strong>{step.day}</strong>
                            <span>{step.text}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>

                  <p className="kili-route__note mb-3">{route.notes}</p>
                  <Link className="text-link" to="/contact">
                    Ask about {route.name}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5" data-reveal>
            <div className="col-lg-8">
              <p className="eyebrow">Before you climb</p>
              <h2 className="mb-0">Practical notes from the desk</h2>
            </div>
          </div>

          <div className="row g-4">
            {practical.map((item, index) => (
              <div
                className="col-md-4"
                key={item.title}
                data-reveal
                style={{ "--reveal-delay": `${index * 90}ms` }}
              >
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-close" data-reveal="fade">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <p className="eyebrow eyebrow--light">Plan your climb</p>
              <h2 className="mb-3">Tell us the month and your preferred route.</h2>
              <p className="mb-0 home-close__copy">
                We reply with a day by day outline, crew details, and a clear
                price for your group size.
              </p>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link className="btn btn-mk-gold" to="/contact">
                Contact the desk
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Kilimanjaro;
